import React, { useState } from "react";
import { url } from "../lib/api";

const ProfileEdit = ({ profile, token, setProfile }) => {
  const [form, setForm] = useState(profile || {});
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Password change state
  const [pwForm, setPwForm] = useState({
    old_password: "",
    new_password: "",
    confirm_new_password: "",
  });
  const [pwMsg, setPwMsg] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const handlePwChange = (e) => {
    setPwForm({ ...pwForm, [e.target.name]: e.target.value });
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    setPwMsg("");
    setPwError("");
    try {
      const res = await fetch(url("/change-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(pwForm),
      });
      if (!res.ok) {
        let msg = "Password change failed";
        try {
          const errData = await res.json();
          if (typeof errData.detail === "string") msg = errData.detail;
          else if (typeof errData.detail === "object")
            msg = JSON.stringify(errData.detail);
        } catch (e) {
          // Ignore JSON parse error, keep default msg
        }
        throw new Error(msg);
      }
      setPwMsg("Password changed successfully!");
      setPwForm({
        old_password: "",
        new_password: "",
        confirm_new_password: "",
      });
    } catch (err) {
      setPwError(
        typeof err.message === "string"
          ? err.message
          : JSON.stringify(err.message)
      );
    }
    setPwLoading(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    setError("");
    try {
      // Always send username for backend validation
      const updatable = { ...form, username: form.username };
      // Cast types for backend
      if (updatable.age) updatable.age = parseInt(updatable.age);
      if (updatable.height) updatable.height = parseFloat(updatable.height);
      if (updatable.weight) updatable.weight = parseFloat(updatable.weight);
      const res = await fetch(url("/profile"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatable),
      });
      if (!res.ok) {
        let msg = "Profile update failed";
        try {
          const errData = await res.json();
          if (typeof errData.detail === "string") msg = errData.detail;
          else if (typeof errData.detail === "object")
            msg = JSON.stringify(errData.detail);
        } catch (e) {
          // ignore JSON parse error, keep default msg
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setProfile(data);
      localStorage.setItem("user_profile", JSON.stringify(data));
      setMsg("Profile updated successfully!");
    } catch (err) {
      setError(
        typeof err.message === "string"
          ? err.message
          : JSON.stringify(err.message)
      );
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-4 text-center">Edit Profile</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input
          name="username"
          placeholder="Username"
          value={form.username || ""}
          disabled
          className="border rounded-lg px-4 py-2 bg-gray-100"
        />
        <input
          name="name"
          placeholder="Name"
          value={form.name || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="age"
          placeholder="Age"
          value={form.age || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="gender"
          placeholder="Gender"
          value={form.gender || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="height"
          placeholder="Height (cm)"
          value={form.height || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="weight"
          placeholder="Weight (kg)"
          value={form.weight || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="activity"
          placeholder="Activity Level"
          value={form.activity || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="goal"
          placeholder="Goal"
          value={form.goal || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="allergies"
          placeholder="Allergies"
          value={form.allergies || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="preferences"
          placeholder="Preferences"
          value={form.preferences || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        <input
          name="diet_category"
          placeholder="Diet Category"
          value={form.diet_category || ""}
          onChange={handleChange}
          className="border rounded-lg px-4 py-2"
        />
        {msg && <div className="text-green-600 text-sm">{msg}</div>}
        {error && <div className="text-red-600 text-sm">{error}</div>}
        <button
          type="submit"
          className="bg-green-500 text-white py-2 rounded-lg font-bold hover:bg-green-600 transition"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Profile"}
        </button>
      </form>

      {/* Password Change Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-xl font-bold mb-2 text-center">Change Password</h3>
        <form onSubmit={handlePwSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            name="old_password"
            placeholder="Current Password"
            value={pwForm.old_password}
            onChange={handlePwChange}
            className="border rounded-lg px-4 py-2"
            required
            autoComplete="current-password"
          />
          <input
            type="password"
            name="new_password"
            placeholder="New Password"
            value={pwForm.new_password}
            onChange={handlePwChange}
            className="border rounded-lg px-4 py-2"
            required
            autoComplete="new-password"
          />
          <input
            type="password"
            name="confirm_new_password"
            placeholder="Confirm New Password"
            value={pwForm.confirm_new_password}
            onChange={handlePwChange}
            className="border rounded-lg px-4 py-2"
            required
            autoComplete="new-password"
          />
          {pwMsg && <div className="text-green-600 text-sm">{pwMsg}</div>}
          {pwError && <div className="text-red-600 text-sm">{pwError}</div>}
          <button
            type="submit"
            className="bg-blue-500 text-white py-2 rounded-lg font-bold hover:bg-blue-600 transition"
            disabled={pwLoading}
          >
            {pwLoading ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileEdit;
